import {
  scoreQueryOptions,
  scoreRefreshIntervalMs,
} from "../scores/scoreQueryOptions";
/**
 * Composes the whole game detail screen: fetches `/event/detail-get`,
 * branches to `ComingSoon`, the header+tabs tree, or, when the read settles
 * with no game, the missing or failed state (ISSUE-1019); drives the tab
 * strip from `?tab=`, polls while live, lays out two columns at 1024px.
 *
 * R11's desktop split is inside the tab content, not the tab strip: the
 * strip and Matchup's own content stay in the LEFT column; when the server
 * offers `summary` and/or `plays`, that content additionally renders
 * un-clicked in the RIGHT column at `lg` and up. Lineup never appears in the
 * right column -- neither the design brief nor R11 names a third region for
 * it. With no side column offered, the left column spans the page at lg and Matchup lays out its own two columns (ISSUE-988 R8).
 * While Box score is the active tab the side column is not drawn and the tab panel spans the page at lg, so a wide stat table is not squeezed into the narrow column (ISSUE-1166).
 *
 * This screen renders inside `apps/app/src/routes/scores.tsx`'s
 * `SectionScreen`, whose parent `AppShell` already wraps every route's
 * content in `main.sm-content-width.px-4` -- this file does not re-apply
 * either class, which would double the horizontal padding.
 *
 * `refreshAfterSeconds` and `odds` are read defensively even though both
 * now ship on the real `/event/detail-get` response, so this screen keeps
 * working unchanged if either field is ever rolled back or is briefly
 * absent from a stale deploy. See ISSUE-180's plan `## 0.2`. The Betting
 * line is ISSUE-985's one line summary built from `odds`, the only line
 * the server sends (ISSUE-1060 dropped ISSUE-988's `matchup.lineSummary`).
 */
import { useEventDetailGet } from "@sportsmet/events-sdk/private";
import { Button, Skeleton, Tabs, type typeTabItem } from "@sportsmet/ui";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import type { typeLiveStateWire } from "../liveState/liveStateTypes";
import { DetailNavBar } from "../scores/DetailNavBar";
import { resolveKickoffLabel } from "../scores/kickoffLabel";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import { BoxScoreTab } from "./BoxScoreTab";
import { ComingSoon } from "./ComingSoon";
import {
  GAME_DETAIL_TABS_STICKY_CLASS,
  GameDetailHeader,
} from "./GameDetailHeader";
import {
  GAME_DETAIL_BACK_LABEL,
  GAME_DETAIL_FAILED_LINE,
  GAME_DETAIL_MISSING_BODY,
  GAME_DETAIL_MISSING_TITLE,
  GAME_DETAIL_RETRY_LABEL,
  GAME_DETAIL_RETRY_LOADING_LABEL,
  isComingSoon,
  resolveActiveTabKey,
  resolveGameDetailFailureKind,
  resolveOfferedTabs,
  type typeGameDetailTabKey,
} from "./gameDetailLogic";
import { InjuriesTab } from "./InjuriesTab";
import { LineupTab } from "./LineupTab";
import { MatchupTab } from "./MatchupTab";
import type { typeEventOdds } from "./oddsLogic";
import { OddsTab } from "./OddsTab";
import { PlaysTab } from "./PlaysTab";
import { SummaryTab } from "./SummaryTab";

// Every branch leaves the same way: clear eventId and tab, keep the rest (R5).
// One 44px row, the back chevron, so a loading, missing or failed game reads
// like the loaded screen's own top row (Scores UX spec v1.0 section 7.1).
const BackToScoresLink = (): ReactNode => (
  <DetailNavBar
    backLabel={GAME_DETAIL_BACK_LABEL}
    clearKeys={["eventId", "tab", "lineupTeam"]}
    testId="game-detail-nav"
  />
);

export const GameDetailScreen = ({
  eventId,
  tab,
  lineupTeam,
}: {
  eventId: string;
  tab: string | undefined;
  /** `?lineupTeam=`, routeState/sectionSearch.ts. Absent means the away
   *  roster (LineupTab's default side). */
  lineupTeam?: "away" | "home" | undefined;
}): ReactNode => {
  // No `from` -- same reasoning as `useOverlay.ts`: this component is
  // rendered under `/scores` today, but pinning `from` would tie it to
  // that one route id and break under the throwaway single-route router
  // `renderInShell` uses in tests.
  const navigate = useNavigate();
  const [injurySelection, setInjurySelection] = useState<{
    eventId: string;
    teamId: string;
  } | null>(null);
  const timeZone = resolveViewerTimeZone();
  const detail = useEventDetailGet(
    { eventId, timeZone },
    {
      ...scoreQueryOptions,
      refetchInterval: (query) =>
        query.state.data?.data?.status === "final" ||
        query.state.data?.data?.status === "canceled"
          ? 120_000
          : scoreRefreshIntervalMs(query.state.data?.data?.refreshAfterSeconds),
    },
  );

  useEffect(() => {
    const code = extractErrorCode(detail.data);
    if (code !== null && detail.data?.data == null)
      showApiError(detail.data, undefined, detail.dataUpdatedAt);
  }, [detail.dataUpdatedAt, eventId]);

  const data = detail.data?.data ?? null;

  const refreshAfterSeconds =
    typeof (data as { refreshAfterSeconds?: unknown } | null)
      ?.refreshAfterSeconds === "number"
      ? (data as { refreshAfterSeconds: number }).refreshAfterSeconds
      : null;

  if (detail.isPending) {
    return (
      <div className="flex flex-col gap-3">
        <BackToScoresLink />
        <Skeleton shape="card" />
        <Skeleton shape="card" />
        <Skeleton shape="card" />
      </div>
    );
  }

  // A settled read with no game. The modal from the effect above has already
  // told the fan what happened. This is what stays on the page once they
  // close it (ISSUE-1019).
  if (data === null) {
    const failureKind = resolveGameDetailFailureKind(
      extractErrorCode(detail.data),
    );
    if (failureKind === "missing")
      return (
        <>
          <BackToScoresLink />
          <div
            data-testid="game-detail-missing"
            className="mx-auto flex max-w-120 flex-col items-center gap-2 py-10 text-center"
          >
            <p className="text-body-strong text-ink">
              {GAME_DETAIL_MISSING_TITLE}
            </p>
            <p className="text-body text-ink-secondary">
              {GAME_DETAIL_MISSING_BODY}
            </p>
          </div>
        </>
      );
    return (
      <>
        <BackToScoresLink />
        <div
          data-testid="game-detail-failed"
          className="mx-auto flex max-w-120 flex-col items-center gap-3 py-10 text-center"
        >
          <p className="text-body text-error">{GAME_DETAIL_FAILED_LINE}</p>
          <Button
            variant="secondary"
            loading={detail.isFetching}
            loadingLabel={GAME_DETAIL_RETRY_LOADING_LABEL}
            onClick={() => void detail.refetch()}
          >
            {GAME_DETAIL_RETRY_LABEL}
          </Button>
        </div>
      </>
    );
  }

  if (isComingSoon(data.layout))
    return (
      <>
        <BackToScoresLink />
        <ComingSoon />
      </>
    );

  const offeredTabs = resolveOfferedTabs(data.tabs);
  const activeTab = resolveActiveTabKey(offeredTabs, tab);
  const odds = (data as { odds?: typeEventOdds }).odds ?? null;
  // ISSUE-1091. Read defensively, same as odds: null unless the game is live.
  // Gated on isLive as the score row is: a game stuck in progress past the
  // stale cutoff reads "Not final" (ISSUE-1088), so its last count, bases or
  // possession is not current and is not drawn.
  const liveState = data.isLive
    ? ((data as { liveState?: typeLiveStateWire | null }).liveState ?? null)
    : null;
  const isLive = data.status === "inProgress";
  // ISSUE-1382. Same gate as liveState: a stale "Not final" game shows no play.
  const lastPlay = data.isLive ? (data.lastPlay ?? null) : null;
  const summaryEntries = data.summary?.entries ?? null;

  // The side column at lg repeats Summary and Plays beside the active tab,
  // but never the one the active tab already shows (spec 7.2, G6), and is
  // not drawn at all beside Box score (ISSUE-1166).
  const offersSummaryOrPlays = offeredTabs.some(
    (t) => t.key === "summary" || t.key === "plays",
  );
  const isSummaryInSideColumn =
    offersSummaryOrPlays && summaryEntries !== null && activeTab !== "summary";
  const isPlaysInSideColumn =
    offeredTabs.some((t) => t.key === "plays") && activeTab !== "plays";
  const isSideColumnShown =
    activeTab !== "boxScore" && (isSummaryInSideColumn || isPlaysInSideColumn);

  const contentForTab = (key: typeGameDetailTabKey): ReactNode => {
    switch (key) {
      case "matchup":
        return (
          <MatchupTab
            sections={data.matchup?.sections ?? []}
            odds={odds}
            liveState={liveState}
            lastPlay={lastPlay}
            isLive={isLive && data.isLive}
            isOddsOffered={offeredTabs.some((t) => t.key === "odds")}
            home={{
              teamId: data.home.teamId,
              name: data.home.name,
              abbreviation: data.home.abbreviation,
              logoUrl: data.home.logoUrl ?? null,
              shortName: data.home.shortName ?? null,
            }}
            away={{
              teamId: data.away.teamId,
              name: data.away.name,
              abbreviation: data.away.abbreviation,
              logoUrl: data.away.logoUrl ?? null,
              shortName: data.away.shortName ?? null,
            }}
            isWide={!isSideColumnShown}
            isScoringSummaryInSideColumn={
              isSideColumnShown && isSummaryInSideColumn
            }
          />
        );
      case "summary":
        return summaryEntries === null ? null : (
          <SummaryTab entries={summaryEntries} />
        );
      case "plays":
        return (
          <PlaysTab
            eventId={eventId}
            isLive={isLive}
            home={{
              teamId: data.home.teamId,
              abbreviation: data.home.abbreviation,
              logoUrl: data.home.logoUrl ?? null,
            }}
            away={{
              teamId: data.away.teamId,
              abbreviation: data.away.abbreviation,
              logoUrl: data.away.logoUrl ?? null,
            }}
          />
        );
      case "injuries":
        return (
          <InjuriesTab
            selectedTeamId={
              injurySelection?.eventId === eventId
                ? injurySelection.teamId
                : null
            }
            onSelectTeam={(teamId) => {
              setInjurySelection({ eventId, teamId });
            }}
            home={{
              teamId: data.home.teamId,
              name: data.home.name,
              abbreviation: data.home.abbreviation,
              logoUrl: data.home.logoUrl ?? null,
            }}
            away={{
              teamId: data.away.teamId,
              name: data.away.name,
              abbreviation: data.away.abbreviation,
              logoUrl: data.away.logoUrl ?? null,
            }}
          />
        );
      case "odds":
        return (
          <OddsTab
            odds={odds}
            home={{
              teamId: data.home.teamId,
              name: data.home.name,
              abbreviation: data.home.abbreviation,
              logoUrl: data.home.logoUrl ?? null,
              shortName: data.home.shortName ?? null,
            }}
            away={{
              teamId: data.away.teamId,
              name: data.away.name,
              abbreviation: data.away.abbreviation,
              logoUrl: data.away.logoUrl ?? null,
              shortName: data.away.shortName ?? null,
            }}
          />
        );
      case "boxScore":
        return (
          <BoxScoreTab
            eventId={eventId}
            isLive={isLive}
            refreshAfterSeconds={refreshAfterSeconds}
            away={{
              teamId: data.away.teamId,
              name: data.away.name,
              abbreviation: data.away.abbreviation,
            }}
            home={{
              teamId: data.home.teamId,
              name: data.home.name,
              abbreviation: data.home.abbreviation,
            }}
          />
        );
      case "lineup":
        return (
          <LineupTab
            eventId={eventId}
            away={{
              teamId: data.away.teamId,
              name: data.away.name,
              abbreviation: data.away.abbreviation,
            }}
            home={{
              teamId: data.home.teamId,
              name: data.home.name,
              abbreviation: data.home.abbreviation,
            }}
            side={lineupTeam ?? "away"}
            onSelectSide={({ side }) =>
              void navigate({
                to: ".",
                search: (prev: unknown) => ({
                  ...(prev as Record<string, unknown>),
                  lineupTeam: side,
                }),
              })
            }
          />
        );
    }
  };

  const items: typeTabItem[] = offeredTabs.map((t) => ({
    value: t.key,
    label: t.label,
    content: contentForTab(t.key),
  }));

  return (
    <>
      <GameDetailHeader
        eventId={eventId}
        kickoffAt={data.kickoffAt}
        timeZone={timeZone}
        status={data.status}
        statusLabel={resolveKickoffLabel({
          status: data.status,
          statusLabel: data.statusLabel,
          kickoffAt: data.kickoffAt,
          timeZone,
          nowMs: Date.now(),
        })}
        isLive={data.isLive}
        winnerTeamId={data.winnerTeamId ?? null}
        isTie={data.isTie}
        liveState={liveState}
        away={{
          teamId: data.away.teamId,
          name: data.away.name,
          abbreviation: data.away.abbreviation,
          score: data.away.score ?? null,
          recordLabel: data.away.recordLabel ?? null,
          pollRankLabel: data.away.pollRankLabel ?? null,
          logoUrl: data.away.logoUrl ?? null,
          primaryColorHex: data.away.primaryColorHex ?? null,
          shortName: data.away.shortName ?? null,
        }}
        home={{
          teamId: data.home.teamId,
          name: data.home.name,
          abbreviation: data.home.abbreviation,
          score: data.home.score ?? null,
          recordLabel: data.home.recordLabel ?? null,
          pollRankLabel: data.home.pollRankLabel ?? null,
          logoUrl: data.home.logoUrl ?? null,
          primaryColorHex: data.home.primaryColorHex ?? null,
          shortName: data.home.shortName ?? null,
        }}
      />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        <div
          data-testid="game-detail-tab-column"
          className={
            isSideColumnShown ? "lg:w-140 lg:shrink-0" : "lg:min-w-0 lg:flex-1"
          }
        >
          <Tabs
            label="Game detail"
            variant="detail"
            listClassName={GAME_DETAIL_TABS_STICKY_CLASS}
            items={items}
            value={activeTab}
            onValueChange={(next) =>
              void navigate({
                to: ".",
                search: (prev: unknown) => ({
                  ...(prev as Record<string, unknown>),
                  tab: next,
                  lineupTeam: undefined,
                }),
              })
            }
          />
        </div>
        {isSideColumnShown ? (
          <div
            className="hidden flex-col gap-4 lg:flex lg:min-w-0 lg:flex-1"
            data-testid="game-detail-desktop-column"
          >
            {isSummaryInSideColumn && summaryEntries !== null ? (
              <SummaryTab entries={summaryEntries} />
            ) : null}
            {isPlaysInSideColumn ? (
              <PlaysTab
                eventId={eventId}
                isLive={isLive}
                home={{
                  teamId: data.home.teamId,
                  abbreviation: data.home.abbreviation,
                  logoUrl: data.home.logoUrl ?? null,
                }}
                away={{
                  teamId: data.away.teamId,
                  abbreviation: data.away.abbreviation,
                  logoUrl: data.away.logoUrl ?? null,
                }}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
};
